/**
 * PlatformSettings.js - Detect and configure platform-specific settings
 * 
 * This module handles detecting the user's platform (desktop, mobile, tablet)
 * and configuring appropriate settings for optimal performance.
 * 
 * MOBILE-FUTURE: This file contains placeholders for mobile-specific settings
 * that will be implemented in the future.
 */

// Define platform types
export const PLATFORMS = {
    DESKTOP: 'desktop',
    MOBILE: 'mobile',
    TABLET: 'tablet'
};

// Quality presets
export const QUALITY_PRESETS = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    ULTRA: 'ultra'
};

// Platform-specific default settings
export const PLATFORM_DEFAULTS = {
    [PLATFORMS.DESKTOP]: {
        quality: QUALITY_PRESETS.HIGH,
        viewDistance: 4,
        fogDensity: 0.005,
        chunkDetail: 1.0,
        shadows: true,
        maxZombies: 10
    },
    
    // MOBILE-FUTURE: These are placeholder settings for future mobile implementation
    [PLATFORMS.MOBILE]: {
        quality: QUALITY_PRESETS.LOW,
        viewDistance: 2,
        fogDensity: 0.02,
        chunkDetail: 0.5,
        shadows: false,
        maxZombies: 3
    },
    
    [PLATFORMS.TABLET]: {
        quality: QUALITY_PRESETS.MEDIUM,
        viewDistance: 3,
        fogDensity: 0.01,
        chunkDetail: 0.7,
        shadows: false,
        maxZombies: 5
    }
};

// Quality preset configurations
export const QUALITY_SETTINGS = {
    [QUALITY_PRESETS.LOW]: {
        textureQuality: 0.5,
        shadowMapSize: 512,
        grassDensity: 0.3,
        particleMultiplier: 0.3,
        antialiasing: false,
        ambientOcclusion: false
    },
    [QUALITY_PRESETS.MEDIUM]: {
        textureQuality: 0.75,
        shadowMapSize: 1024,
        grassDensity: 0.6,
        particleMultiplier: 0.7,
        antialiasing: true,
        ambientOcclusion: false
    },
    [QUALITY_PRESETS.HIGH]: {
        textureQuality: 1.0,
        shadowMapSize: 2048,
        grassDensity: 1.0,
        particleMultiplier: 1.0,
        antialiasing: true,
        ambientOcclusion: true
    },
    [QUALITY_PRESETS.ULTRA]: {
        textureQuality: 1.0,
        shadowMapSize: 4096,
        grassDensity: 1.5,
        particleMultiplier: 1.5,
        antialiasing: true,
        ambientOcclusion: true
    }
};

/**
 * Detect the user's platform
 * @returns {string} Platform type
 */
export function detectPlatform() {
    // Simple detection based on user agent and screen size
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent);
    const isTablet = isMobile && Math.min(window.innerWidth, window.innerHeight) > 600;
    
    if (isMobile) {
        return isTablet ? PLATFORMS.TABLET : PLATFORMS.MOBILE;
    }
    
    return PLATFORMS.DESKTOP;
}

/**
 * Get settings for the current platform
 * @param {string} platform - Platform type
 * @param {string} qualityOverride - Optional quality override
 * @returns {Object} Platform settings
 */
export function getPlatformSettings(platform = null, qualityOverride = null) {
    const detectedPlatform = platform || detectPlatform();
    const platformDefaults = PLATFORM_DEFAULTS[detectedPlatform] || PLATFORM_DEFAULTS[PLATFORMS.DESKTOP];
    
    // Allow quality override
    const quality = qualityOverride || platformDefaults.quality;
    const qualitySettings = QUALITY_SETTINGS[quality] || QUALITY_SETTINGS[QUALITY_PRESETS.MEDIUM];
    
    // Merge platform defaults with quality settings
    return {
        ...platformDefaults,
        ...qualitySettings,
        platform: detectedPlatform,
        qualityPreset: quality
    };
}

// MOBILE-FUTURE: Functions to implement for mobile support

/**
 * Create touch controls for mobile devices
 * @param {HTMLElement} container - Container element for controls
 * MOBILE-FUTURE: Implement this function
 */
export function createTouchControls(container) {
    // This will be implemented in the future
    console.log('Touch controls not yet implemented');
}

/**
 * Apply mobile-specific optimizations
 * @param {Object} renderer - THREE.js renderer
 * @param {Object} scene - THREE.js scene
 * MOBILE-FUTURE: Implement this function
 */
export function applyMobileOptimizations(renderer, scene) {
    // This will be implemented in the future
    console.log('Mobile optimizations not yet implemented');
}

// Export default settings
export default {
    detect: detectPlatform,
    getSettings: getPlatformSettings,
    PLATFORMS,
    QUALITY_PRESETS
}; 